import { CustomKpi } from 'common/types/customKpi';
import { KPI } from 'common/types/kpi';
import { KPI_CATALOGUE_ROUTES } from 'settings/routes';

export const KPI_CATALOGUE_PATH = '/kpi-catalogue';
export const STUDY_SEARCH_PARAM = 'study';

/** What the catalogue's right pane shows; parsed from the path after `/kpi-catalogue`. */
export type CatalogueSelection =
  | { kind: 'none' }
  | { kind: 'kpi'; id: string }
  | { kind: 'custom'; uuid: string }
  | { kind: 'edit'; uuid: string }
  | { kind: 'new' };

const NONE: CatalogueSelection = { kind: 'none' };

export const parseCatalogueSelection = (splat = ''): CatalogueSelection => {
  const [section, id, action, ...rest] = splat.split('/').filter(Boolean);

  if (rest.length) return NONE;
  if (section === KPI_CATALOGUE_ROUTES.NEW && !id) return { kind: 'new' };
  if (section === KPI_CATALOGUE_ROUTES.KPI && id && !action) return { kind: 'kpi', id };
  if (section === KPI_CATALOGUE_ROUTES.CUSTOM && id && !action) return { kind: 'custom', uuid: id };
  if (section === KPI_CATALOGUE_ROUTES.CUSTOM && id && action === KPI_CATALOGUE_ROUTES.EDIT) return { kind: 'edit', uuid: id };
  return NONE;
};

const selectionSegments = (selection: CatalogueSelection): string[] => {
  switch (selection.kind) {
    case 'kpi':
      return [KPI_CATALOGUE_ROUTES.KPI, selection.id];
    case 'custom':
      return [KPI_CATALOGUE_ROUTES.CUSTOM, selection.uuid];
    case 'edit':
      return [KPI_CATALOGUE_ROUTES.CUSTOM, selection.uuid, KPI_CATALOGUE_ROUTES.EDIT];
    case 'new':
      return [KPI_CATALOGUE_ROUTES.NEW];
    default:
      return [];
  }
};

export const catalogueSelectionPath = (selection: CatalogueSelection, studyUuid?: string): string => {
  const path = [KPI_CATALOGUE_PATH, ...selectionSegments(selection).map(encodeURIComponent)].join('/');
  return studyUuid ? `${path}?${new URLSearchParams({ [STUDY_SEARCH_PARAM]: studyUuid })}` : path;
};

export const normalizeQuery = (query: string): string => query.trim().toLowerCase();

export const matchesQuery = (label: string, normalizedQuery: string): boolean => label.toLowerCase().includes(normalizedQuery);

/** Newest first (CK-M6). */
export const sortCustomKpis = (customKpis: CustomKpi[]): CustomKpi[] =>
  [...customKpis].sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));

export const filterCustomKpis = (customKpis: CustomKpi[], query: string): CustomKpi[] => {
  const normalized = normalizeQuery(query);
  return normalized ? customKpis.filter(({ name }) => matchesQuery(name, normalized)) : customKpis;
};

/**
 * Keeps KPIs whose label matches, with all their options, and KPIs with a matching option,
 * narrowed to the matching options (CK-M3).
 */
export const filterCatalogueKpis = (kpis: KPI[], query: string): KPI[] => {
  const normalized = normalizeQuery(query);
  if (!normalized) return kpis;

  return kpis.reduce<KPI[]>((acc, kpi) => {
    if (matchesQuery(kpi.kpi_label, normalized)) return [...acc, kpi];

    const options = kpi.question_options?.filter(({ option_label }) => matchesQuery(option_label, normalized)) || [];
    return options.length ? [...acc, { ...kpi, question_options: options }] : acc;
  }, []);
};

export interface KpiLookup {
  kpi: KPI;
  optionIdentifier?: string;
}

/** Finds the KPI a catalogue `kpi/:id` selection points at, by KPI or option identifier. */
export const findKpiOrOption = (kpis: KPI[], id: string): KpiLookup | undefined => {
  for (const kpi of kpis) {
    if (kpi.kpi_identifier === id) return { kpi };
    if (kpi.question_options?.some(({ option_identifier }) => option_identifier === id)) return { kpi, optionIdentifier: id };
  }
  return undefined;
};
